import React, { useState } from 'react';
import Hero from '../components/Hero';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { companyInfo } from '../mockData';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    propertyType: '',
    serviceInterest: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    // Show success message
    toast.success('Quote request submitted!', {
      description: 'We\'ll get back to you within 24 hours.'
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      propertyType: '',
      serviceInterest: '',
      message: ''
    });
  };

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <Hero
        subtitle="Contact Us"
        title="Let's Discuss Your Property's Needs"
        description="Get a customized quote for your commercial property. We'll respond within 24 hours with a detailed proposal."
        image="https://images.unsplash.com/photo-1630060041646-3ba002aa7d37"
        height="min-h-[500px]"
      />

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-navy-primary mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Ready to discuss your property's maintenance needs? Reach out directly 
                or fill out the quote request form, and we'll get back to you promptly.
              </p>

              <div className="space-y-6">
                <Card className="p-4 hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-ice-blue/50 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-navy-primary" size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-navy-primary mb-1">Phone</div>
                      <a 
                        href={`tel:${companyInfo.phone}`}
                        className="text-gray-700 hover:text-navy-primary transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-ice-blue/50 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-navy-primary" size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-navy-primary mb-1">Email</div>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-gray-700 hover:text-navy-primary transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-ice-blue/50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-navy-primary" size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-navy-primary mb-1">Address</div>
                      <p className="text-gray-700">
                        {companyInfo.address}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-ice-blue/50 flex items-center justify-center flex-shrink-0">
                      <Clock className="text-navy-primary" size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-navy-primary mb-1">Hours</div>
                      <p className="text-gray-700 whitespace-pre-line text-sm">
                        {companyInfo.hours}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-navy-primary mb-6">
                  Request a Quote
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="mt-2"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select 
                        value={formData.propertyType}
                        onValueChange={(value) => handleSelectChange('propertyType', value)}
                        required
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retail">Retail/Commercial Plaza</SelectItem>
                          <SelectItem value="office">Office Complex</SelectItem>
                          <SelectItem value="industrial">Industrial/Warehouse</SelectItem>
                          <SelectItem value="residential">Multi-Residential</SelectItem>
                          <SelectItem value="portfolio">Multi-Site Portfolio</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="serviceInterest">Service Interest *</Label>
                      <Select 
                        value={formData.serviceInterest}
                        onValueChange={(value) => handleSelectChange('serviceInterest', value)}
                        required
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="landscape">Landscape Management</SelectItem>
                          <SelectItem value="snow">Snow & Ice Management</SelectItem>
                          <SelectItem value="seasonal">Seasonal Cleanups</SelectItem>
                          <SelectItem value="upkeep">Site Upkeep</SelectItem>
                          <SelectItem value="full">Full-Service Package</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Details</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-2 min-h-[120px]"
                      placeholder="Tell us about your property, specific needs, or any questions you have..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-navy-primary hover:bg-navy-secondary text-white"
                  >
                    Submit Quote Request
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="py-16 bg-navy-primary text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">
            Winter Weather Emergency?
          </h3>
          <p className="text-ice-blue/90 text-lg mb-6">
            Our snow and ice management team is available 24/7 during storm events
          </p>
          <a href={`tel:${companyInfo.phone}`}>
            <Button 
              size="lg" 
              className="bg-white text-navy-primary hover:bg-ice-blue transition-all duration-300"
            >
              <Phone className="mr-2" size={20} />
              Call Emergency Line: {companyInfo.phone}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
